/*
 * Copied from https://github.com/taitems/email-to-name/blob/master/email-to-name.js
 */

interface Params {
  removeNumbers: boolean;
  removePlusWords: boolean;
  titleCase: boolean;
  caseMc: boolean;
  caseLetterApostrophe: boolean;
  uppercaseGenerationalNumbers: boolean;
  commaPrependGenerationalPhrase: boolean;
  appendPeriodToTitlePrefix: boolean;
  commonPersonalIdentifiers: string[];
  reverseCommonEmailAddresses: boolean;
  companyNames: string[];
}

const defaults: Params = {
  removeNumbers: true,
  removePlusWords: true,
  titleCase: true,
  caseMc: true,
  caseLetterApostrophe: true,
  uppercaseGenerationalNumbers: true,
  commaPrependGenerationalPhrase: true,
  appendPeriodToTitlePrefix: true,
  commonPersonalIdentifiers: ['hello', 'me', 'email', 'contact'],
  reverseCommonEmailAddresses: true,
  companyNames: [],
};
const titles = ['mr', 'mrs', 'ms', 'dr', 'prof'];
const suffixes = ['jr', 'jnr', 'sr', 'snr'];
const suffixesUpper = ['ii', 'iii', 'iv'];

function matchIdentifierToList(str: string, identifiers: string[]) {
  if (!identifiers || !identifiers.length) {
    return false;
  }
  let identifier = str.split('@')[0];
  let match = false;
  for (let i = 0; i < identifiers.length; i++) {
    if (identifier === identifiers[i]) {
      match = true;
      break;
    }
  }
  return match;
}

function reverseDomainAndIdentifier(str: string) {
  let provider = str.split('@')[1];
  let host = provider.split('.')[0];
  return host;
}

function titleCase(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map(function(word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

const emailToName = function(str: string, params?: Params): string {
  let output = '';

  let settings = Object.assign({}, defaults, params);

  // Is the email address identified a common "hello" type string?
  let identifierIsCommon = matchIdentifierToList(str, settings.commonPersonalIdentifiers);

  // Is the email address from someone with a personal domain?
  // Checks email address for company name as the identifier
  let domainIsIdentifier = matchIdentifierToList(str, settings.companyNames);

  // Get the true identifier
  // -> `companyname@personaldomain.com`. to `personaldomain`
  // -> `john.smith@gmail.com` to `john.smith`
  if (settings.reverseCommonEmailAddresses && (identifierIsCommon || domainIsIdentifier)) {
    output = reverseDomainAndIdentifier(str);
  } else {
    output = str.split('@')[0];
  }

  // Drop everything after the '+'
  // `john.smith+test` to `john.smith`
  if (settings.removePlusWords) {
    output = output.split('+')[0];
  }

  if (settings.removeNumbers) {
    output = output.replace(/\d/g, '');
  }

  // Replace periods with spaces
  // `john.smith` to `john smith`
  output = output.replace(/\./g, ' ');

  // Replace underscores with spaces
  // `john_smith` to `john smith`
  output = output.replace(/_/g, ' ');

  // Replace duplicate strings from inside
  // `john    smith` to `john smith`
  output = output.replace(/\s\s+/g, ' ');

  // Replace whitespace from both sites of string
  // `  john smith  ` to `john smith`
  output = output.trim();

  // Title case
  // `john smith` to `John Smith`
  if (settings.titleCase) {
    output = titleCase(output);
  }

  // Handle Generational (The Third) names
  // `John Smith Iii` to `John Smith III`
  if (settings.uppercaseGenerationalNumbers) {
    suffixesUpper.forEach(function(suffix) {
      let rx = new RegExp('\\s(' + suffix + ')$', 'gi');
      output = output.replace(rx, function(s) {
        return s.toUpperCase();
      });
    });
  }

  // Handle 'Jr/Sr' names
  // `John Smith Jr` to `John Smith, Jr.`
  if (settings.commaPrependGenerationalPhrase) {
    suffixes.forEach(function(suffix) {
      let rx = new RegExp('\\s(' + suffix + ')$', 'gi');
      output = output.replace(rx, function(s) {
        return ',' + s + '.';
      });
    });
  }

  // Handle title prefixes names
  // `Dr John Smith` to `Dr. John Smith`
  if (settings.appendPeriodToTitlePrefix) {
    titles.forEach(function(prefix) {
      let rx = new RegExp('^(' + prefix + ')\\s', 'gi');
      output = output.replace(rx, function(s) {
        return s.replace(' ', '. ');
      });
    });
  }

  // Handle 'Mc' names
  // `Marty Mcfly` to `Marty McFly`
  if (settings.titleCase && settings.caseMc) {
    output = output.replace(/Mc(.)/g, function(m, m1) {
      return 'Mc' + m1.toUpperCase();
    });
  }

  // Handle 'O'Connor' type names
  // `Flannery O'connor` to `Flannery O'Connor`
  if (settings.titleCase && settings.caseLetterApostrophe) {
    output = output.replace(/[A-Z]'(.)/g, function(m, m1) {
      return "O'" + m1.toUpperCase();
    });
  }

  return output;
};

export default emailToName;
